"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import palette from "@/styles/site-palette.module.css";
import { cn } from "@/lib/utils";

type TocItem = { id: string; text: string; level: number };
const HEADING_OFFSET = 112;

export default function ArticleToc({ placement = "floating", contentKey, panel = false }: { placement?: "floating" | "inline" | "outside"; contentKey?: string; panel?: boolean } = {}) {
  const t = useTranslations("site.common");
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const root = document.querySelector<HTMLElement>("[data-reading-body] .wmde-markdown") ?? document.querySelector<HTMLElement>(".wmde-markdown");
    const heads = Array.from(root?.querySelectorAll<HTMLElement>("h2[id], h3[id]") ?? []);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setItems(heads.map((head) => ({ id: head.id, text: head.textContent?.trim() ?? "", level: head.tagName === "H2" ? 2 : 3 })));

    let frame = 0;
    const update = () => {
      frame = 0;
      let current = heads[0]?.id ?? "";
      for (const head of heads) {
        if (head.getBoundingClientRect().top > HEADING_OFFSET) break;
        current = head.id;
      }
      setActiveId(current);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("hashchange", schedule);

    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    if (root) observer?.observe(root);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("hashchange", schedule);
      observer?.disconnect();
    };
  }, [contentKey]);

  useEffect(() => {
    const list = listRef.current;
    const link = list?.querySelector<HTMLElement>('[data-active="true"]');
    if (!list || !link || !list.clientHeight) return;
    const bounds = list.getBoundingClientRect();
    const target = link.getBoundingClientRect();
    if (target.top < bounds.top + 12) list.scrollTop -= bounds.top + 12 - target.top;
    else if (target.bottom > bounds.bottom - 12) list.scrollTop += target.bottom - bounds.bottom + 12;
  }, [activeId]);

  if (items.length < 2) return null;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const el = document.getElementById(id);
    if (!el) return;
    event.preventDefault();
    setActiveId(id);
    el.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "start" });
    history.replaceState(history.state, "", `#${encodeURIComponent(id)}`);
    setMenuOpen(false);
  };

  const links = items.map((item) => (
    <li key={item.id}>
      <a href={`#${encodeURIComponent(item.id)}`} onClick={(event) => handleClick(event, item.id)}
        aria-current={activeId === item.id ? "location" : undefined} data-active={activeId === item.id}
        className={`block border-l py-1.5 text-sm leading-5 wrap-anywhere transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none ${item.level === 3 ? "pl-[26px]" : "pl-[14px]"} ${activeId === item.id ? "border-brand text-brand" : "border-border text-muted-foreground hover:text-brand"}`}>
        {item.text}
      </a>
    </li>
  ));

  if (placement === "outside") return <>
    <aside data-doc-outside-toc className="absolute bottom-6 left-full top-6 hidden w-56 pl-6 min-[1920px]:block">
      <nav aria-label={t("toc")} className="sticky top-[89px] flex h-[calc(100dvh-113px)] min-w-0 flex-col pt-6 text-sm">
        <div className="mb-2 shrink-0 font-medium leading-5 text-foreground">{t("onThisPage")}</div>
        <ul ref={listRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-3 pr-2 [scrollbar-width:thin] [scrollbar-color:var(--border)_transparent]">{links}</ul>
      </nav>
    </aside>
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger render={<Button variant="ghost" size="sm" className="min-[1920px]:hidden" />}>
        {t("onThisPage")}<ChevronDown className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent align="end" className={cn(palette.palette, "min-[1920px]:hidden")}>
        <PopoverTitle>{t("onThisPage")}</PopoverTitle>
        <nav aria-label={t("toc")}><ul className="max-h-[min(60dvh,24rem)] overflow-y-auto overscroll-contain pr-2 [scrollbar-width:thin]">{links}</ul></nav>
      </PopoverContent>
    </Popover>
  </>;

  return (
    <nav aria-label={t("toc")} className={`hidden min-w-0 flex-col text-sm xl:flex ${panel ? "sticky top-[89px] h-[calc(100dvh-113px)] self-start rounded-lg border border-border bg-card/90 p-5 shadow-sm sm:p-6" : placement === "inline" ? "sticky top-24 h-[calc(100dvh-7rem)] self-start pb-4 pt-8" : "fixed right-6 top-24 h-[calc(100dvh-7rem)] w-[248px] pb-4 pt-8"}`}>
      <div className="mb-2 shrink-0 font-medium leading-5 text-foreground">{t("onThisPage")}</div>
      <ul ref={listRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-3 pr-2 [scrollbar-width:thin] [scrollbar-color:var(--border)_transparent]">
        {links}
      </ul>
    </nav>
  );
}
